import { ReactNode, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {
  HistoryIcon,
  MarkerLookupIcon,
  SearchIcon,
  StorageIcon,
  UploadIcon
} from "@/assets/icons";
import LokasiIntelligenceBvartaLogo from "@/assets/svg/lokasiIntelligenceBvartaLogo.svg?react";
import { Divider } from "@/components/ui/Divider";
import { MESSAGE } from "@/constants/message";
import { useAuth } from "@/hooks/auth/useAuth";
import { useMap } from "@/hooks/map/core/useMapContext";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useHandleClearView from "@/hooks/ui/useHandleClearView";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { setGeneralSummary } from "@/store/slice/analysisSummary";
import { clearCurrRunningAnalysisId } from "@/store/slice/spatialSlice";
import {
  ESidebarMenuType,
  resetUIDataConfigExceptSidebarMenu,
  setAnalysisResultPopup,
  setShowAnalysisSummaryPopup,
  setShowTemplateModal,
  setSideBarMenuTypePopupDisplay,
  toggleDatasetExplorerPopup,
  toggleShowSearchLocationPopup,
  toggleSidebarMenu
} from "@/store/slice/uiSlice";
import { AreaInputType } from "@/types/spatial";

import { twMerge } from "tailwind-merge";

import NavItem from "./navItem";
import SidebarFooter from "./SidebarFooter";
import type { SidebarFooterPopoverType } from "./types";

export type MenuItemType = {
  name: string;
  icon: ReactNode;
  type: string | null;
  onClick: () => void;
  isActive?: boolean;
  hide: boolean;
};

const SideBar = () => {
  const { isAuthenticated } = useAuth();
  const userPermissions = useGetUserPermission({ enabled: isAuthenticated });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showConfirmation } = useConfirmationModal();
  const {
    isTemplateEnabled,
    isSearchLocationEnabled,
    isAnalysisHistoryEnabled
  } = useAppFeatures();
  const {
    handleResetDatasetToHistoryDataset,
    removeAllCatchmentMarkers,
    renderDrawPolygon
  } = useMap();
  const { handleCloseDetailView } = useHandleClearView();

  const [activePopover, setActivePopover] =
    useState<SidebarFooterPopoverType>("");

  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get("preview") === "true";

  const {
    showAnalysisResultPopup,
    showAnalysisSummaryPopup,
    sidebarMenu,
    showSearchLocationPopup
  } = useSelector((state: RootState) => state.ui);

  const { spatialConfig } = useSelector((state: RootState) => state.spatial);

  const handleClearAIConversation = () => {
    localStorage.removeItem("lokasi_ai_conversations");
  };

  // Currently no requirement for expand the sidebar mas, so keep it empty func
  const toggleSidebar = () => {
    dispatch(toggleSidebarMenu(!sidebarMenu));
  };

  const handleChangeTab = (
    callback: () => void,
    type: ESidebarMenuType,
    showConfirmationForSummaryPopup: boolean = true
  ) => {
    const handleConfirm = () => {
      dispatch(resetUIDataConfigExceptSidebarMenu());
      callback();
    };

    const shouldShowConfirmation =
      showAnalysisResultPopup ||
      (showAnalysisSummaryPopup && showConfirmationForSummaryPopup);

    const shouldConfirmLeaveAnalysis =
      showAnalysisSummaryPopup &&
      [
        ESidebarMenuType.DATASET,
        ESidebarMenuType.DATASET_EXPLORER,
        ESidebarMenuType.SEARCH
      ].includes(type);

    if (!shouldShowConfirmation) {
      handleConfirm();
      return;
    }

    showConfirmation({
      title: shouldConfirmLeaveAnalysis
        ? MESSAGE.LEAVE_ANALYSIS_CONFIRMATION_TITLE
        : MESSAGE.UNSAVED_CHANGES_TITLE,
      message: shouldConfirmLeaveAnalysis
        ? MESSAGE.LEAVE_ANALYSIS_CONFIRMATION_DESCRIPTION
        : MESSAGE.CONFIRMATION_LEAVE,
      cancelLabel: "",
      confirmLabel: "Leave",
      onConfirm: () => {
        if (spatialConfig?.areaInput?.value === AreaInputType.POLYGON) {
          renderDrawPolygon();
        }
        toast.dismiss();
        removeAllCatchmentMarkers();
        handleCloseDetailView();
        handleResetDatasetToHistoryDataset();
        handleConfirm();
        handleClearAIConversation();
      }
    });
  };

  const getMenuList = (isPreviewMode: boolean): MenuItemType[] => [
    {
      name: "Analysis",
      type: ESidebarMenuType.SPATIAL,
      icon: <MarkerLookupIcon />,
      isActive: showAnalysisResultPopup,
      hide: !userPermissions[EModuleName.SPATIAL_ANALYSIS][
        EModulePermission.VIEW
      ],
      onClick: () =>
        handleChangeTab(
          () => {
            if (isPreviewMode) handleResetDatasetToHistoryDataset();
            if (showAnalysisSummaryPopup) {
              dispatch(setGeneralSummary(null));
              dispatch(setShowAnalysisSummaryPopup(false));
              dispatch(setAnalysisResultPopup(true));
            } else {
              navigate("/");
              dispatch(
                setSideBarMenuTypePopupDisplay(ESidebarMenuType.SPATIAL)
              );

              if (isTemplateEnabled) {
                const dontShowAgainItem = localStorage.getItem(
                  "template-modal-dont-show-again"
                );
                const dontShowAgain = dontShowAgainItem
                  ? JSON.parse(dontShowAgainItem)
                  : false;

                if (!dontShowAgain) dispatch(setShowTemplateModal(true));
              }
            }
          },
          ESidebarMenuType.SPATIAL,
          false
        )
    },
    {
      name: "Dataset Explorer",
      type: ESidebarMenuType.DATASET_EXPLORER,
      icon: <StorageIcon />,
      hide: !userPermissions[EModuleName.DATASET_EXPLORER][
        EModulePermission.VIEW
      ],
      onClick: () =>
        handleChangeTab(() => {
          if (isPreviewMode) handleResetDatasetToHistoryDataset();
          if (showAnalysisSummaryPopup) {
            dispatch(setGeneralSummary(null));
            dispatch(setShowAnalysisSummaryPopup(false));
            dispatch(setAnalysisResultPopup(false));
          }
          handleClearAIConversation();
          dispatch(toggleDatasetExplorerPopup(true));
          navigate("/");
        }, ESidebarMenuType.DATASET_EXPLORER)
    },
    {
      name: "Dataset Management",
      type: ESidebarMenuType.DATASET,
      icon: <UploadIcon />,
      hide: !userPermissions[EModuleName.DATASET_MANAGEMENT][
        EModulePermission.VIEW
      ],
      isActive: location.pathname.includes(
        `${import.meta.env.VITE_BASE_PATH}dataset-management`
      ),
      onClick: () =>
        handleChangeTab(() => {
          if (isPreviewMode) handleResetDatasetToHistoryDataset();
          if (showAnalysisSummaryPopup) {
            dispatch(setGeneralSummary(null));
            dispatch(setShowAnalysisSummaryPopup(false));
            dispatch(setAnalysisResultPopup(false));
          }
          handleClearAIConversation();
          dispatch(toggleDatasetExplorerPopup(false));
          dispatch(clearCurrRunningAnalysisId());
          toast.dismiss();
          navigate("/dataset-management");
        }, ESidebarMenuType.DATASET)
    },
    {
      name: "Search",
      type: ESidebarMenuType.SEARCH,
      icon: <SearchIcon />,
      isActive: showSearchLocationPopup,
      hide: !isSearchLocationEnabled,
      onClick: () =>
        handleChangeTab(() => {
          if (isPreviewMode) handleResetDatasetToHistoryDataset();
          if (showAnalysisSummaryPopup || showSearchLocationPopup) {
            dispatch(setGeneralSummary(null));
            dispatch(setShowAnalysisSummaryPopup(false));
            dispatch(setAnalysisResultPopup(false));
          }
          handleClearAIConversation();
          dispatch(toggleShowSearchLocationPopup());
          navigate("/");
        }, ESidebarMenuType.SEARCH)
    },
    {
      name: "Historical Analysis",
      type: ESidebarMenuType.ANALYSIS_HISTORY,
      icon: <HistoryIcon />,
      hide: !isAnalysisHistoryEnabled,
      isActive: location.pathname.includes(
        `${import.meta.env.VITE_BASE_PATH}analysis-history`
      ),
      onClick: () =>
        handleChangeTab(() => {
          if (isPreviewMode) handleResetDatasetToHistoryDataset();
          if (showAnalysisSummaryPopup) {
            dispatch(setGeneralSummary(null));
            dispatch(setShowAnalysisSummaryPopup(false));
            dispatch(setAnalysisResultPopup(false));
          }
          handleClearAIConversation();
          dispatch(toggleDatasetExplorerPopup(false));
          dispatch(clearCurrRunningAnalysisId());
          toast.dismiss();
          navigate("/analysis-history");
        }, ESidebarMenuType.ANALYSIS_HISTORY)
    }
  ];

  const renderLogo = () => (
    <Link
      className={twMerge(
        "flex justify-center mb-4",
        sidebarMenu ? "w-full justify-start pl-[20px]" : "w-full justify-center"
      )}
      to="/"
    >
      <LokasiIntelligenceBvartaLogo
        title="LOKASI Intelligence Bvarta Logo"
        className="h-auto w-[23px]"
      />
    </Link>
  );

  return (
    <section className="flex bg-white">
      <div
        className={twMerge(
          "bg-primary-700 min-h-screen transition-all text-gray-100 flex flex-col border-r border-gainsboro w-16 items-center",
          sidebarMenu ? "w-[264px]" : "w-[60px] items-center"
        )}
      >
        <div className="flex flex-col h-full w-full justify-between my-4">
          <div>
            {renderLogo()}
            <Divider className="bg-white-smoke w-full h-[4px]" />
            <div className="flex flex-col justify-between h-full items-center">
              <div
                className={twMerge(
                  "mt-4 flex flex-col space-y-1.5 gap-3 relative",
                  sidebarMenu ? "flex items-start w-full px-4" : ""
                )}
              >
                {getMenuList(isPreviewMode)
                  .filter((item) => !item.hide)
                  .map((menuItem, i) => (
                    <NavItem
                      menuItem={menuItem}
                      index={i}
                      isExpand={sidebarMenu}
                      key={menuItem.name}
                    />
                  ))}
              </div>
            </div>
          </div>
          <SidebarFooter
            isExpanded={sidebarMenu}
            toggleSidebar={toggleSidebar}
            activePopover={activePopover}
            setActivePopover={setActivePopover}
          />
        </div>
      </div>
    </section>
  );
};

export default SideBar;
